import React, { useState, useEffect, useRef } from "react";
import Header from "../../components/Header/Header";
import { Search } from "../../components/Search/Search";
import Card from "../../components/Cards/Card";
import "./MentorPal.css";
import FooterWithTimer from "../../components/Footer/FooterWithTimer";
import Footer from "../../components/Footer/Footer";
import PanelCard from "../../components/Cards/PanelCard";
import { appendHomePageDataToUrl } from "../../utils/appendHomePageDataToUrl";
import { fetchHomePageData } from "../../components/fetchHomePageData";
import BackToTop from "../../components/BackToTop/BackToTop";
import { getEmailFromUserId, mentorCardClickEventBuilder, mentorPanelClickEventBuilder, mentorSearchEventBuilder, sendEvent } from "../../utils/event-helpers";
import { useWithSessionData } from "../../hooks/use-with-session-data";

export interface Mentor {
  _id: string;
  name: string;
  title: string;
  mentorUrl: string;
  thumbnail: string;
  keywords: string[];
}

export interface MentorPanel{
  _id: string;
  mentors: string[];
  org: string;
  panelUrl: string;
  subject: string;
  subtitle: string;
  title: string;
}

export interface MentorTileDisplayData{
  title: string;
  image: string;
  number: number;
  status: number;
}

export interface SelectedMentorContent{
  url: string;
  title: string;
  mentors: Mentor[];
}


const MentorPal = ({
  showExtras = true,
  isMentorsPage = false,
  isResearchParticipant = true,
}) => {
  const [query, setQuery] = useState("");
  const [activeMentorIds, setActiveMentorIds] = useState([]);
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [panels, setPanels] = useState<MentorPanel[]>([]);
  const [filteredMentors, setFilteredMentors] = useState<Mentor[]>([]);
  const [isIntroCollapsed, setIsIntroCollapsed] = useState(false);
  const [selectedMentorContent, setSelectedMentorContent] = useState<SelectedMentorContent | null>(null);
  const [shouldRunTimer, setShouldRunTimer] = useState(false);
  const [timerId, setTimerId] = useState(null);
  const [userStatus, setUserStatus] = useState(1);
  const { userId, sessionId, sessionIdLoaded, userIdLoaded } = useWithSessionData();

  // Refs for debounce functionality
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const filteredMentorsRef = useRef<Mentor[]>([]);

  const data = async () => await fetchHomePageData(process.env.PATHWAYS_ORG_ID);

  const sortAndFilterByIdOrder = (idArray, objects) => {
    return objects
      .filter((obj) => idArray.includes(obj._id))
      .sort((a, b) => idArray.indexOf(a._id) - idArray.indexOf(b._id));
  };

  const USER_STATUS = {
    SELECT_MENTOR: 1,
    INTERVIEW_MENTOR: 2,
    GET_RESOURCES: 3,
    REVISIT: 4,
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!mentors || !sessionIdLoaded || !userIdLoaded) return;
    // Filter by search query
    const newFilteredMentors = mentors.filter((mentor) => {
      if (!mentor || !mentor.keywords) return false;

      const matchesName = mentor.name
        .toLowerCase()
        .includes(query.toLowerCase());

      const matchesTitle = mentor.title
        .toLowerCase()
        .includes(query.toLowerCase());
      return (matchesName || matchesTitle);
    });

    const excludedNames = [
      "Mark Hammel",
      "Amber Jolly",
      "William Purpura",
      "Jose Zavala",
      "Darla Holland",
      "Susan Ramsey",
    ];

    const finalFilteredMentors = newFilteredMentors.filter(
      (mentor) => !excludedNames.includes(mentor.name)
    );

    const sorted_Order_Mentors: Mentor[] = sortAndFilterByIdOrder(
      activeMentorIds,
      finalFilteredMentors
    );

    const mentorsWithUpdatedUrls: Mentor[] = sorted_Order_Mentors.map((mentor) => {
      const url = new URL(mentor.mentorUrl);
      url.searchParams.set("userid", userId);
      url.searchParams.set("sessionId", sessionId);
      url.searchParams.set("userEmail", getEmailFromUserId(userId));
      return {
        ...mentor,
        mentorUrl: url.toString(), // updated URL
      };
    });
    filteredMentorsRef.current = mentorsWithUpdatedUrls;
    setFilteredMentors(mentorsWithUpdatedUrls);
  }, [query, mentors, activeMentorIds, sessionId, userId, sessionIdLoaded, userIdLoaded]);

  useEffect(() => {
    setShouldRunTimer(true);
    const fetchData = async () => {
      try {
        const homePageData = await data();
        setMentors(homePageData.mentors);
        setPanels(homePageData.panels);
        setActiveMentorIds(homePageData.activeMentorIds);
      } catch (error) {
        console.error("Failed to fetch home page data:", error);
      }
    };

    fetchData();
  }, []);

  const stopTimer = () => {
    if (timerId) {
      clearInterval(timerId);
      setTimerId(null);
    }
  };

  const handleSearchChange = (event) => {
    const newQuery = event.target.value;
    setQuery(newQuery);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const eventData = mentorSearchEventBuilder(userId, sessionId, newQuery, filteredMentorsRef.current, {});
      sendEvent(eventData);
    }, 500);
  };

  const toggleIntroCollapse = () => {
    setIsIntroCollapsed(!isIntroCollapsed);
  };

  const handleMentorCardSelect = async (url: string, title: string, selectedMentor: Mentor) => {
    const mentorIds = [selectedMentor._id];
    const updatedUrl = appendHomePageDataToUrl(mentorIds, url);
    setSelectedMentorContent({ url: updatedUrl, title, mentors: [selectedMentor] });

    if (userStatus === USER_STATUS.SELECT_MENTOR) {
      const newStatus = USER_STATUS.INTERVIEW_MENTOR;
      setUserStatus(newStatus);
    }
  };

  const handlePanelCardSelect = async (url: string, title: string, selectedPanel: MentorPanel) => {
    const panelMentors = mentors.filter((mentor) => selectedPanel.mentors.includes(mentor._id));
    const updatedUrl = appendHomePageDataToUrl(panelMentors.map((mentor) => mentor._id), selectedPanel.panelUrl);
    setSelectedMentorContent({ url: updatedUrl, title, mentors: panelMentors });

    if (userStatus === USER_STATUS.SELECT_MENTOR) {
      const newStatus = USER_STATUS.INTERVIEW_MENTOR;
      setUserStatus(newStatus);
    }
  };

  const mentorTileDisplayData: MentorTileDisplayData[] = [
    {
      title: "Select a mentor",
      image: "./images/selective.png",
      number: 1,
      status: USER_STATUS.SELECT_MENTOR,
    },
    {
      title: "Interview mentor(s)",
      image: "./images/chat.png",
      number: 2,
      status: USER_STATUS.INTERVIEW_MENTOR,
    },
    {
      title: "Get personalized resources",
      image: "./images/human-resources.png",
      number: 3,
      status: USER_STATUS.GET_RESOURCES,
    },
    {
      title: "Revisit for updates",
      image: "./images/returning-visitor.png",
      number: 4,
      status: USER_STATUS.REVISIT,
    },
  ];

  const handleMentorCardClick = (mentor: Mentor) => {
    const eventData = mentorCardClickEventBuilder(userId, sessionId, mentor, {});
    sendEvent(eventData);
    handleMentorCardSelect(mentor.mentorUrl, mentor.name, mentor);
  };

  const handlePanelCardClick = (panel: MentorPanel) => {
    const panelMentors = mentors.filter((mentor) => panel.mentors.includes(mentor._id));
    const eventData = mentorPanelClickEventBuilder(userId, sessionId, panel, panelMentors, {});
    sendEvent(eventData);
    handlePanelCardSelect(panel.panelUrl, panel.title, panel);
  };

  return (
    <div className={`mentorpal ${isMentorsPage ? "mentors-page" : ""}`} style={{
      height:"100%"
    }}>
      {showExtras && <Header />}

      {showExtras && !selectedMentorContent && (
        <div className="intro-section">
          <div
            className={`intro-tiles-container ${
              isIntroCollapsed ? "collapsed" : ""
            }`}
          >
            {mentorTileDisplayData.map((mentor, index) => (
              <React.Fragment key={mentor.number}>
                <div
                  className={`intro-tile ${
                    mentor.status === userStatus ? "highlighted" : ""
                  }`}
                >
                  <div
                    className="intro-tile-image"
                    style={{
                      backgroundImage: `linear-gradient(rgba(245, 246, 247, ${
                        mentor.status === userStatus ? "0.9" : "0.8"
                      }), rgba(245, 246, 247, ${
                        mentor.status === userStatus ? "0.9" : "0.8"
                      })), url(${mentor.image})`,
                      backgroundSize: "25%",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                    }}
                  ></div>

                  <div className="intro-tile-number">{mentor.number}</div>
                  <h1 className="intro-tile-title">{mentor.title}</h1>
                </div>

                {index < mentorTileDisplayData.length - 1 && (
                  <div
                    className={`intro-tile-arrow ${
                      mentor.status === userStatus
                        ? "intro-tile-arrow-highlighted"
                        : ""
                    }`}
                  >
                    →
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div
            className={`collapse-arrow ${isIntroCollapsed ? "collapsed" : ""}`}
            onClick={toggleIntroCollapse}
            aria-label={isIntroCollapsed ? "Expand intro" : "Collapse intro"}
          >
            <span className="arrow-icon"></span>
          </div>
        </div>
      )}

      {!selectedMentorContent && (
        <div className="mentor-search-container">
          <div className="mentor-search">
            <Search query={query} handleSearchChange={handleSearchChange} />
          </div>
        </div>
      )}

      {selectedMentorContent ? (
        // When content is selected, only show the iframe

        <div className="embedded-content-frame">
          <iframe
            src={`${selectedMentorContent.url}&disableHeader=true`}
            title={selectedMentorContent.title}
            className="content-iframe"
          />
        </div>
      ) : (
        // When no content is selected, show navigation and mentor sections
        <>
          <div className="section-links">
            <a href="#mentor-panels">Mentor Panels</a>
            <a href="#individual-mentors">Individual Mentors</a>
          </div>

          <div className="mentor-grid">
            <div className="mentor-panels" id="mentor-panels">
              {panels.map((card) => (
                <PanelCard
                  key={card._id}
                  mentorPanel={card}
                  isMentorsPage={isMentorsPage}
                  onClick={() => {
                    handlePanelCardClick(card);
                  }}
                />
              ))}
            </div>

            <div className="mentor-cards" id="individual-mentors">
              {filteredMentors.map((mentor) => (
                <Card
                  key={mentor._id}
                  mentor={mentor}
                  isMentorsPage={isMentorsPage}
                  onClick={() => {
                    handleMentorCardClick(mentor);
                  }}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {showExtras &&
        (isResearchParticipant ? (
          <FooterWithTimer
            setSelectedMentorContent={setSelectedMentorContent}
            selectedMentorContent={selectedMentorContent}
            shouldRunTimer={shouldRunTimer}
            stopTimer={stopTimer}
          />
        ) : (
          <Footer />
        ))}

      <BackToTop />
    </div>
  );
};

export default MentorPal;
